import { useState, useCallback } from 'react';

/**
 * Custom hook for handling authentication forms (login, signup, password reset)
 * @param {string} formType - Type of form: 'login', 'signup', 'reset'
 * @returns {Object} Form state and handlers
 */
export const useAuth = (formType = 'login') => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });

  const [formErrors, setFormErrors] = useState({});

  /**
   * Handle input changes
   * @param {Event} event - Input change event
   */
  const handleChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [formErrors]);

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} Is email valid
   */
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  /**
   * Validate form based on form type
   * @returns {boolean} Is form valid
   */
  const validateForm = useCallback(() => {
    const errors = {};

    // Email validation (required for all forms)
    if (!formData.email) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation (required for login and signup)
    if (formType !== 'reset') {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 6) {
        errors.password = 'Password must be at least 6 characters';
      }
    }

    // Additional validation for signup
    if (formType === 'signup') {
      if (!formData.firstName) {
        errors.firstName = 'First name is required';
      }
      if (!formData.lastName) {
        errors.lastName = 'Last name is required';
      }
      if (!formData.confirmPassword) {
        errors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, formType]);

  /**
   * Handle form submission
   * @param {Object} data - Form data to submit
   * @param {string} type - Form type
   * @returns {Promise} Promise that resolves on successful submission
   */
  const handleSubmit = useCallback(async (data, type) => {
    // This is a demo implementation
    // In a real application, this would make API calls to your backend
    
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // Simulate API call
        if (type === 'login') {
          // Demo: Accept any email/password for demo purposes
          if (data.email && data.password) {
            resolve({ 
              success: true, 
              message: 'Login successful',
              user: { email: data.email, name: `${data.firstName || 'User'} ${data.lastName || ''}`.trim() }
            });
          } else {
            reject(new Error('Invalid credentials'));
          }
        } else if (type === 'signup') {
          // Demo: Accept any valid signup data
          if (data.email && data.password && data.firstName && data.lastName) {
            resolve({ 
              success: true, 
              message: 'Account created successfully',
              user: { email: data.email, name: `${data.firstName} ${data.lastName}` }
            });
          } else {
            reject(new Error('Please fill in all required fields'));
          }
        } else if (type === 'reset') {
          // Demo: Accept any valid email for password reset
          if (data.email) {
            resolve({ 
              success: true, 
              message: 'Password reset email sent'
            });
          } else {
            reject(new Error('Please enter a valid email address'));
          }
        }
      }, 1000); // Simulate network delay
    });
  }, []);

  /**
   * Reset form data
   */
  const resetForm = useCallback(() => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: ''
    });
    setFormErrors({});
  }, []);

  return {
    formData,
    formErrors,
    handleChange,
    validateForm,
    handleSubmit,
    resetForm
  };
};
